import React, { useCallback, useState } from "react";
import TypeAndEntityPicker from "./TypeAndEntityPicker";
import MergePreview from "./MergePreview";
import MergeConfirm from "./MergeConfirm";
import MergeResult from "./MergeResult";
import MergeProgress from "./MergeProgress";
import { fetchPreview } from "./mergeApi";
import { useMergeRunner } from "./useMergeRunner";

const STEPS = ["Select", "Preview", "Confirm", "Result"];

function StepIndicator({ step }) {
  return (
    <ol className="breadcrumb">
      {STEPS.map((label, idx) => {
        const n = idx + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={label} className={`breadcrumb-item ${active ? "active fw-bold" : ""} ${done ? "text-success" : ""}`}>
            {done && <i className="fa fa-check me-1" />}
            {label}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * MergeConsole — generic admin entity-merge flow. Four steps: pick type +
 * source/destination → preview diff (with the self-expert-source guard) →
 * type-MERGE confirmation → result. Drives Admin::Api::MergesController.
 *
 * `initialType`/`initialSourceId`/`initialDestinationId` let entity pages deep-link
 * in with the type and either (or both) sides prefilled; `initialSourceLabel` /
 * `initialDestinationLabel` (optional) render a prefilled side as "Name (#id)"
 * instead of the bare id. The admin contextual panel's Merge button supplies only
 * one side (source, or destination for a self-expert) plus its label; the
 * MediaOutlet show page's modal supplies only the source.
 */
function MergeConsole({
  initialType,
  initialSourceId,
  initialSourceLabel,
  initialDestinationId,
  initialDestinationLabel,
}) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(initialType || "");
  const [sourceId, setSourceId] = useState(initialSourceId ? String(initialSourceId) : "");
  const [destinationId, setDestinationId] = useState(initialDestinationId ? String(initialDestinationId) : "");
  // Labels live here (not inside TypeAndEntityPicker) so they survive step changes:
  // the picker unmounts once the flow advances past step 1, and Back must restore the
  // TRUE current selection, not the original initial*Label prefill.
  const [sourceLabel, setSourceLabel] = useState(initialSourceLabel || null);
  const [destinationLabel, setDestinationLabel] = useState(initialDestinationLabel || null);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // The merge runner owns the POST → poll-with-backoff → retry lifecycle; the
  // console just reflects its phase/outcome.
  const { phase, outcome, elapsedSeconds, run, retry, reset: resetRunner } = useMergeRunner();

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const data = await fetchPreview({ type, sourceId, destinationId });
      setPreview(data);
      // The preview's names are authoritative; overwrite the picker labels (which
      // may have arrived unverified via URL prefill) so Back shows the truth.
      if (data?.source?.name) setSourceLabel(data.source.name);
      if (data?.destination?.name) setDestinationLabel(data.destination.name);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  }, [type, sourceId, destinationId]);

  const goToPreview = useCallback(() => {
    setStep(2);
    loadPreview();
  }, [loadPreview]);

  const runMerge = useCallback(() => {
    setStep(4);
    run({ type, sourceId, destinationId });
  }, [run, type, sourceId, destinationId]);

  const reset = useCallback(() => {
    setStep(1);
    setDestinationId("");
    setDestinationLabel(null);
    setPreview(null);
    setPreviewError(null);
    resetRunner();
  }, [resetRunner]);

  const stepTitle = { 1: "Select entities", 2: "Preview changes", 3: "Confirm merge", 4: "Result" }[step];

  return (
    <div className="card">
      <div className="card-header bg-white">
        <h1 className="h4 mb-2">
          <i className="fa fa-object-group me-2" />
          Merge Console &mdash; {stepTitle}
        </h1>
        <StepIndicator step={step} />
      </div>
      <div className="card-body">
        {step === 1 && (
          <TypeAndEntityPicker
            type={type}
            setType={setType}
            sourceId={sourceId}
            setSourceId={setSourceId}
            destinationId={destinationId}
            setDestinationId={setDestinationId}
            sourceLabel={sourceLabel}
            setSourceLabel={setSourceLabel}
            destinationLabel={destinationLabel}
            setDestinationLabel={setDestinationLabel}
            onPreview={goToPreview}
          />
        )}

        {step === 2 && (
          <MergePreview
            preview={preview}
            loading={previewLoading}
            error={previewError}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <MergeConfirm
            source={{
              id: sourceId,
              name: preview?.source?.name ?? sourceLabel,
              href: preview?.source?.links?.id ?? preview?.source?.links?.slug ?? null,
            }}
            destination={{
              id: destinationId,
              name: preview?.destination?.name ?? destinationLabel,
              href: preview?.destination?.links?.id ?? preview?.destination?.links?.slug ?? null,
            }}
            executing={phase === "running"}
            onBack={() => setStep(2)}
            onExecute={runMerge}
          />
        )}

        {step === 4 &&
          (phase === "running" ? (
            <MergeProgress seconds={elapsedSeconds} />
          ) : (
            <MergeResult outcome={outcome} onRetry={retry} onReset={reset} />
          ))}
      </div>
    </div>
  );
}

export default MergeConsole;
